import { PlusIcon } from "lucide-react";
import { useRef, useState } from "react";
import { FieldPath, SubmitHandler } from "react-hook-form";
import { toast } from "sonner";
import { useMutation } from "urql";

import {
    TournamentForm,
    TournamentFormRef,
    convertFormToGraphQL,
} from "@/components/forms/TournamentForm";
import { TournamentInput, zTournamentInput } from "@/components/forms/validation/tournament";
import { buttonVariants } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { graphql } from "@/gen";
import { cn } from "@/utils";

const createTournamentMutation = graphql(`
    mutation createTournament($input: CreateTournamentInput!) {
        createTournament(input: $input) {
            id
            ...useTournament_Tournament
        }
    }
`);
export const TournamentCreateDialog = () => {
    const [_res, createTournament] = useMutation(createTournamentMutation);
    const [isOpen, setIsOpen] = useState(false);
    const formRef = useRef<TournamentFormRef>(null);

    const onSubmit: SubmitHandler<TournamentInput> = async input => {
        const standingsCalculation = convertFormToGraphQL(input.standingsCalculation);

        const result = await createTournament({
            input: {
                title: input.title,
                location: input.location,
                shortTitle: input.shortTitle,
                description: input.description,
                startAt: input.startAt.toISOString(),
                endAt: input.endAt.toISOString(),
                heroImageId: input.heroImageId,
                thumbnailImageId: input.thumbnailImageId,
                format: input.format,
                players: input.players?.map(p => ({ playerId: p.id })),
                previousWinnerPlayerId: input.previousWinnerPlayerId || null,
                isInternalOnly: input.isInternalOnly,
                isHidden: input.isHidden,
                manualStandings: input.manualStandings,
                manualStandingsList: input.manualStandings
                    ? input.manualStandingsList
                          .filter(entry => entry.player != null)
                          .map(entry => ({
                              playerId: entry.player!.id,
                              points: entry.points,
                              rankPoints: entry.rankPoints,
                          }))
                    : null,
                countryStandingsEnabled: input.countryStandingsEnabled,
                countryStandingsTopPlayers: input.countryStandingsTopPlayers,
                standingsCalculation: standingsCalculation ?? null,
                chesscomInternalSlug: input.chesscomInternalSlug,
                beforeLayoutTemplateId: input.beforeLayoutTemplateId || null,
                duringLayoutTemplateId: input.duringLayoutTemplateId || null,
                afterLayoutTemplateId: input.afterLayoutTemplateId || null,
            },
        });

        if (result.error) {
            toast.error("Unable to create tournament", { description: result.error.message });
            for (const error of result.error.graphQLErrors) {
                formRef.current?.setError(error.name as FieldPath<TournamentInput>, {
                    message: error.message,
                });
            }
        } else {
            toast.success("Tournament was created");
            formRef.current?.reset();
            setIsOpen(false);
        }
    };

    const defaultValues = zTournamentInput.parse({
        title: "",
        location: null,
        shortTitle: "",
        startAt: new Date(),
        endAt: new Date(),
        heroImageId: null,
        thumbnailImageId: null,
        badgeImageId: null,
        format: null,
        players: [],
        description: null,
        previousWinnerPlayerId: null,
        isInternalOnly: false,
        isHidden: false,
        manualStandings: false,
        manualStandingsList: [],
        countryStandingsEnabled: false,
        countryStandingsTopPlayers: null,
        tournamentSeriesId: null,
        standingsCalculation: null,
        chesscomInternalSlug: null,
        beforeLayoutTemplateId: null,
        duringLayoutTemplateId: null,
        afterLayoutTemplateId: null,
    });

    return (
        <Dialog open={isOpen} onOpenChange={() => setIsOpen(!isOpen)}>
            <DialogTrigger className={cn(buttonVariants())}>
                <PlusIcon />
            </DialogTrigger>
            <DialogContent
                className="max-h-[80vh] w-full max-w-5xl overflow-y-auto"
                aria-describedby={undefined}
            >
                <DialogHeader>
                    <div className="mr-4 flex items-center justify-between">
                        <DialogTitle>Create tournament</DialogTitle>
                    </div>
                </DialogHeader>
                <TournamentForm
                    ref={formRef}
                    onSubmit={onSubmit}
                    defaultValues={defaultValues}
                    isCreateMode={true}
                />
            </DialogContent>
        </Dialog>
    );
};
